// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { useEffect, type ReactNode } from "react";

import { t } from "../lib/i18n.js";
import { Logo } from "./Logo.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { WindowDragStrip } from "./WindowChrome.js";

/** Centered card for the signed-out screens. */
export function AuthLayout({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} · ${t("app.name")}`;
  }, [title]);
  return (
    <div className="relative isolate flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-bg px-4 py-12">
      {/* A soft indigo glow behind the form, as on Linear's sign-in. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-48 left-1/2 -z-10 h-[32rem] w-[48rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl"
      />
      <WindowDragStrip />
      <ThemeToggle className="absolute top-4 right-4" />
      <main className="w-full max-w-[22rem]">
        <p className="mb-6 flex items-center justify-center">
          <Logo className="h-7" />
        </p>
        <h1 className="mb-8 text-center text-xl font-semibold tracking-[-0.015em] text-fg">{title}</h1>
        <div className="[&_button[type=submit]]:h-10 [&_button[type=submit]]:w-full [&_.g-input]:h-10">{children}</div>
        {footer && <div className="mt-8 text-center text-[13px] text-fg-muted">{footer}</div>}
      </main>
    </div>
  );
}

/** A message at the top of a form. Errors are announced to screen readers immediately. */
export function FormAlert({
  tone,
  children,
}: {
  tone: "error" | "info" | "success";
  children: ReactNode;
}) {
  const color =
    tone === "error"
      ? "text-danger"
      : tone === "success"
        ? "text-success"
        : "text-fg";
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`mb-4 rounded-lg select-text border border-border bg-surface px-3 py-2.5 text-sm ${color}`}
    >
      {children}
    </div>
  );
}

export const linkClass =
  "font-medium text-accent underline-offset-2 hover:underline";
